import styled from 'styled-components';

import { mediaMin, aboveMin } from '@spa/shared/theme';

export const FooterWrapper = styled.footer`
  width: 100%;
  background: ${props => props.theme.footer.background};
  padding: ${props => props.theme.space(9)} 0;
  font-size: ${props => props.theme.rem(18)};

  ${mediaMin.extraSmallBreakpoint`
    padding: ${props => props.theme.space(3)} 0;
  `}
`;

export const InnerFooter = styled.div`
  max-width: 1100px;
  margin: 0 auto;

  ${mediaMin.largeBreakpoint`
    padding-left: ${props => props.theme.space(10)};
    padding-right: ${props => props.theme.space(10)};
  `}

  ${mediaMin.extraSmallBreakpoint`
    display: flex;
    flex-direction: column;
    padding-left: ${props => props.theme.space(4)};
    padding-right: ${props => props.theme.space(4)};
  `}

  ${aboveMin.extraLargeBreakpoint`
    max-width: 1200px;
  `}
`;

export const FooterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  border-top: 1px solid ${props => props.theme.footer.line};
  padding-top: ${props => props.theme.space(5)};
  margin-top: ${props => props.theme.space(12)};

  a,
  .copy-right,
  .termly-display-preferences,
  .terms-of-service,
  .privacy-policy {
    font-family: IntervalNextReg, sans-serif;
    font-size: ${props => props.theme.rem(18)};
    color: ${props => props.theme.footer.link};
    text-decoration: none;
    cursor: pointer;

    ${mediaMin.extraSmallBreakpoint`
      font-size: ${props => props.theme.rem(14)};
      text-align: center;
    `}
  }

  & > * {
    ${mediaMin.extraSmallBreakpoint`
        flex-basis: 100%;
        justify-content: center;

        &:first-child {
            margin-bottom: ${props => props.theme.space(4)};
            display: flex;
            align-items: center;

            svg {
              margin-right: ${props => props.theme.space(3)};
            }
        }
    `}
  }

  ${mediaMin.extraSmallBreakpoint`
    order: 3;
    margin-top: 0;
    padding-top: ${props => props.theme.space(4)};
  `}
`;

export const Grid = styled.ul`
  --gap: 30px;
  --itemWith: 20%;
  display: flex;
  flex-wrap: wrap;
  margin: calc(var(--gap) / -2);

  & > * {
    margin: calc(var(--gap) / 2);
    flex-grow: 1;
    flex-basis: calc(var(--itemWith) - var(--gap));

    ${mediaMin.smallBreakpoint`
        flex-basis: calc(50% - var(--gap));
    `}

    ${mediaMin.extraSmallBreakpoint`
        flex-basis: 100%;
        margin: 0;
    `}
  }

  ${mediaMin.extraSmallBreakpoint`
    padding: ${props => props.theme.space(3)} ${props => props.theme.space(4)} ${props => props.theme.space(2)};
    order: 1;
  `}
`;

export const GridColumn = styled.li`
  padding-top: ${props => props.theme.space(12)};

  ${mediaMin.extraSmallBreakpoint`
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
    padding: ${props => props.theme.space(4)} 0;
    border-bottom: 1px solid ${props => props.theme.footer.line};
  `}
`;

export const HiddenCheckbox = styled.input`
  display: none;

  ${mediaMin.extraSmallBreakpoint`
    &:checked ~ ul {
      margin-top: ${props => props.theme.space(4)};
      max-height: 100%;
    }

    &:checked ~ label {
      &:after {
        transform: rotate(-45deg);
      }

      &:before {
        transform: rotate(45deg);
      }
    }
  `}
`;

export const CollapsibleSection = styled.ul`
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.gap(4, 'column')};
  padding-top: ${props => props.theme.space(8)};

  ${mediaMin.extraSmallBreakpoint`
    max-height: 0;
    overflow: hidden;
    padding-top: 0;
    ${({ theme }) => theme.gap(2, 'column')};
  `}
`;

export const Label = styled.label`
  font-family: IntervalNextReg, sans-serif;
  font-weight: 600;
  font-size: ${props => props.theme.rem(18)};
  font-weight: 600;
  color: ${props => props.theme.footer.title};

  ${mediaMin.extraSmallBreakpoint`
    font-size: ${props => props.theme.rem(18)};
    font-weight: 500;
    display: block;
    cursor: pointer;

    &:after {
      content: ' ';
      top: ${props => props.theme.space(6)};
      position: absolute;
      display: block;
      right: 6px;
      transform: rotate(45deg);
      width: 10px;
      height: 2px;
      background-color: ${props => props.theme.footer.title};
    }

    &:before {
      content: ' ';
      top: ${props => props.theme.space(6)};
      position: absolute;
      display: block;
      transform: rotate(-45deg);
      right: 0;
      width: 10px;
      height: 2px;
      background-color: ${props => props.theme.footer.title};
    }
  `}
`;

export const Item = styled.li`
  display: flex;

  a,
  span {
    cursor: pointer;
    font-family: IntervalNextReg, sans-serif;
    font-size: ${props => props.theme.rem(14)};
    color: ${props => props.theme.footer.link};
    text-decoration: none;

    ${mediaMin.extraSmallBreakpoint`
      font-size: ${props => props.theme.rem(14)};
      margin-left: ${props => props.theme.space(10)};
      padding: ${props => props.theme.space(2)} 0;
      border-top: 1px solid ${props => props.theme.footer.line};
      width: 100%;
      height: 32px;
      align-items: center;
      display: flex;
    `}
  }
`;

export const CopyRight = styled.div`
  span {
    display: flex;
    align-items: center;

    svg {
      margin-right: ${props => props.theme.space(2)};
    }
  }
`;

export const TermsAndPrivacy = styled.div`
  display: flex;

  .terms-of-service,
  .privacy-policy,
  .termly-display-preferences {
    margin-left: ${props => props.theme.space(5)};
  }

  ${mediaMin.extraSmallBreakpoint`
    border-top: 1px solid ${props => props.theme.footer.line};
    padding-bottom: ${props => props.theme.space(4)};
    padding-top: ${props => props.theme.space(4)};

    .privacy-policy,
    .termly-display-preferences {
      border-left: 1px solid ${props => props.theme.footer.line};
      padding-left: ${props => props.theme.space(4)};
    }
  `}
`;

export const SocialRow = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: ${props => props.theme.space(7)};
  border-bottom: 1px solid ${props => props.theme.footer.line};

  & a + a {
    margin-left: ${props => props.theme.space(4)};
  }

  svg {
    path {
      opacity: 1;
      fill: ${props => props.theme.footer.text};
    }
  }

  ${mediaMin.extraSmallBreakpoint`
    order: 2;
    border-bottom: none;
    padding-bottom: ${props => props.theme.space(8)};
    padding-top: ${props => props.theme.space(10)};
    align-items: center;
    justify-content: center;
  `}
`;

export const PaymentMethods = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.gap(10)};
  width: 100%;
  margin-bottom: ${props => props.theme.space(10)};

  ${mediaMin.smallBreakpoint`
    padding: ${props => props.theme.space(2)} ${props => props.theme.space(3)};
    flex-wrap: wrap;
    ${({ theme }) => theme.gap(4)};
    height: 116px;
    margin-bottom: 0;
  `}
`;
